import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { OVERVIEW_ROUTE } from '@rci/self-serve-fido/app.constant';
import { modalWindowConfig } from '@rci/self-serve-fido/shared/modal-window.config';
import { ViewContainerRefService } from '@rci/self-serve-fido/shared/view-container-ref/view-container-ref.service';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { ModalInjectableData } from '@rogers/cdk/common/classes';
import { ModalService } from '@rogers/cdk/modal';
import { AccountListFacadeService, UserAccount } from '@rogers/self-serve-common/account';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AccountSelectorModalComponent } from './account-selector-modal.component';

@Injectable({
  providedIn: 'root',
})
export class AccountSelectorService {
  constructor(
    private readonly accountListFacadeService: AccountListFacadeService,
    private readonly accountDetailService: AccountDetailService,
    private readonly modalService: ModalService,
    private readonly router: Router,
    private readonly userInformationStateFacade: UserInformationStateFacade,
    private readonly viewContainerRefService: ViewContainerRefService,
  ) {}

  private _showAccountSelectorModal = true;

  get showAccountSelectorModal() {
    return this._showAccountSelectorModal;
  }

  set showAccountSelectorModal(value) {
    this._showAccountSelectorModal = value;
  }

  selectAccount$(
    page: string,
    activeAccountsOnly = false,
    excludePrepaidAccounts = false,
  ): Observable<UrlTree | boolean> {
    return this.sortAccounts$(activeAccountsOnly, excludePrepaidAccounts).pipe(
      take(1),
      switchMap(accounts => {
        if (accounts.length > 0) {
          let selectedAccountNumber = accounts[0].accountNumber;
          if (!this.showAccountSelectorModal) {
            this.showAccountSelectorModal = true;
            return of(true);
          }
          if (accounts.length === 1) {
            return this.updateSelectedAccountNumber(selectedAccountNumber);
          }
          return this.modalService
            .open(
              AccountSelectorModalComponent,
              this.viewContainerRefService.viewContainerRef,
              modalWindowConfig,
              new ModalInjectableData({ accounts, page }),
            )
            .afterClosed()
            .pipe(
              take(1),
              switchMap(result => {
                if (!!result && typeof result === 'string') {
                  selectedAccountNumber = result;
                }
                return this.updateSelectedAccountNumber(selectedAccountNumber);
              }),
            );
        }
        this.showAccountSelectorModal = false;
        return of(this.router.parseUrl(OVERVIEW_ROUTE));
      }),
    );
  }

  sortAccounts$(activeAccountsOnly = false, excludePrepaidAccounts = false): Observable<UserAccount[]> {
    return this.accountListFacadeService.accountList$.pipe(
      map(accounts => {
        let activeAccounts: UserAccount[] = [],
          suspendedAccounts: UserAccount[] = [],
          cancelledAccounts: UserAccount[] = [],
          sortedAccounts: UserAccount[];
        sortedAccounts = accounts.filter(account => !excludePrepaidAccounts || !account.isPrepaidAccount);
        if (sortedAccounts.length > 1) {
          sortedAccounts.sort((account1, account2) => (account1.accountNumber < account2.accountNumber ? -1 : 1));
          activeAccounts = sortedAccounts.filter(account => account.isActiveAccount);
          if (!activeAccountsOnly) {
            if (activeAccounts.length < sortedAccounts.length) {
              suspendedAccounts = sortedAccounts.filter(account => account.isSuspendedAccount);
            }

            if (activeAccounts.length + suspendedAccounts.length < sortedAccounts.length) {
              cancelledAccounts = sortedAccounts.filter(account => account.isCancelledAccount);
            }
          }

          sortedAccounts = [...activeAccounts, ...suspendedAccounts, ...cancelledAccounts];
        }

        return sortedAccounts;
      }),
    );
  }

  private updateSelectedAccountNumber(selectedAccountNumber) {
    this.userInformationStateFacade.updateSelectedAccountNumber(selectedAccountNumber);
    this.accountDetailService.fetchAccountDetail$(selectedAccountNumber).pipe(take(1)).subscribe();
    return of(true);
  }
}
